import React, { useMemo } from 'react';

import { useFulfillmentContext } from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { useBalanceSheet } from '@/shared/hooks/useBalanceSheet.ts';
import { ManualFinanceSettings } from '@/shared/components/FinanceSettings/ManualFinanceSettings.tsx';
import { SummaryItems } from '@/shared/components/SummaryItems.tsx';
import { useTranslation } from 'react-i18next';
import { useSectionsValidity } from '../hooks/useSectionsValidity.ts';
import { useFulfillmentSectionsErrors } from '../hooks/useFulfillmentSectionsErrors.ts';
import { useFulfillmentSummary } from '@/clients/order/useFulfillmentSummary.ts';
import { useMaybeFinanceSettingsLocalState } from '@/shared/components/FinanceSettings/context/FinanceSettingsLocalStateContext.tsx';
import { cn } from '@/shared/utils/cn.ts';

interface SummaryProps {
    disabled?: boolean;
}

export const Summary: React.FC<SummaryProps> = ({ disabled }) => {
    const { order, productGroup, isSubmitted } = useFulfillmentContext();

    const { data: fulfillmentSummary, isUpdating } = useFulfillmentSummary();
    const { t } = useTranslation();

    const balanceSheetMap = useBalanceSheet(fulfillmentSummary.organizations.flatMap(({ totals }) => totals) || []);
    const organizationsFinanceSettings =
        useMaybeFinanceSettingsLocalState()?.organizationsFinanceSettings ?? fulfillmentSummary.organizations;

    const errors = useMemo(
        () =>
            organizationsFinanceSettings.flatMap(org => {
                return org.financeSettings.flatMap(({ selected, transactionalRole }) => {
                    const field = `paymentTerm-${org.orgId}-${transactionalRole}`;
                    return selected.billingMethod !== 'PARTNER_INVOICE' && selected.paymentTermId === null
                        ? [
                              {
                                  field,
                                  message: t('errorMessages.form.required'),
                                  scrollToElement: () => {
                                      document
                                          .querySelector(`[name="${field}"]`)
                                          ?.scrollIntoView({ behavior: 'smooth' });
                                  },
                              },
                          ]
                        : [];
                });
            }) || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [organizationsFinanceSettings]
    );

    useSectionsValidity('summary', !errors.length);
    useFulfillmentSectionsErrors(errors);

    if (!order || !fulfillmentSummary) {
        return;
    }

    return (
        <>
            <div className="mb-2 flex flex-col gap-6">
                <ManualFinanceSettings
                    groups={organizationsFinanceSettings}
                    disabled={disabled}
                    showErrorMessages={isSubmitted}
                    context={'FULFILLMENT'}
                    className={cn('transition-opacity duration-500', isUpdating && 'opacity-50')}
                    pricingStrategy={productGroup.type}
                />
                <SummaryItems balanceSheetMap={balanceSheetMap} />
            </div>
        </>
    );
};
